import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import type { AvatarColor, AvatarSize, AvatarVariant } from './types';

type VariantColorConfig = {
  backgroundColor: string;
  color: string;
};

function getColorStyles(
  theme: Theme,
  color: AvatarColor,
  variant: AvatarVariant,
): VariantColorConfig {
  if (color === 'grey') {
    return {
      backgroundColor: theme.colors.background.overlay,
      color: theme.colors.text.primary,
    };
  }

  const configs: Record<Exclude<AvatarColor, 'grey'>, Record<AvatarVariant, VariantColorConfig>> = {
    blue: {
      subtle: { backgroundColor: theme.colors.info.subtle, color: theme.colors.info.text },
      solid: { backgroundColor: theme.colors.info.solid, color: theme.colors.info.onInfo },
    },
    green: {
      subtle: { backgroundColor: theme.colors.brand.subtle, color: theme.colors.brand.text },
      solid: { backgroundColor: theme.colors.brand.solid, color: theme.colors.brand.onBrand },
    },
    orange: {
      subtle: { backgroundColor: theme.colors.warning.subtle, color: theme.colors.warning.text },
      solid: { backgroundColor: theme.colors.warning.solid, color: theme.colors.warning.onWarning },
    },
    red: {
      subtle: { backgroundColor: theme.colors.error.subtle, color: theme.colors.error.text },
      solid: { backgroundColor: theme.colors.error.solid, color: theme.colors.error.onError },
    },
    purple: {
      subtle: { backgroundColor: theme.colors.accent.subtle, color: theme.colors.accent.text },
      solid: { backgroundColor: theme.colors.accent.solid, color: theme.colors.accent.onAccent },
    },
  };

  return configs[color][variant];
}

export const SIZE_CONFIG: Record<AvatarSize, { dimension: number; iconSize: number }> = {
  sm: { dimension: 32, iconSize: 16 },
  md: { dimension: 40, iconSize: 20 },
  lg: { dimension: 48, iconSize: 24 },
  xl: { dimension: 56, iconSize: 24 },
  '2xl': { dimension: 64, iconSize: 32 },
};

const TRANSIENT_PROPS = new Set(['$color', '$variant', '$size']);

export const StyledAvatar = styled('span', {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop),
})<{
  $color: AvatarColor;
  $variant: AvatarVariant;
  $size: AvatarSize;
}>(({ theme, $color, $variant, $size }) => {
  const colorStyles = getColorStyles(theme, $color, $variant);
  const { dimension, iconSize } = SIZE_CONFIG[$size];

  return {
    width: dimension,
    height: dimension,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.full,
    ...colorStyles,
    fontFamily: 'DM Sans, sans-serif',
    userSelect: 'none',
    boxSizing: 'border-box',
    overflow: 'hidden',

    svg: {
      width: iconSize,
      height: iconSize,
      flexShrink: 0,
    },
  };
});

export const AvatarImage = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});
